<template>
    <div class="app-header gradient px-6" :class="{
        'pt-6': ! short && ! long,
        'py-6': short && ! long,
        'pt-6 pb-8': long,
    }">
        <div class="max-w-none m-auto" :class="`md:${mediumMaxWidth}`">

            <slot name="navigation">
                <div class="flex justify-between items-center">
                    <h6 v-if="page" class="text-white font-semibold">{{ page }}</h6>
                    <div class="w-full max-w-200" v-else>
                        <h5 data-testid="title" class="font-zen-dots text-white">
                            <AppLogo />    
                        </h5>
                    </div>

                    <template>
                        <div class="md:hidden">
                            <i @click="openMenu" class="cursor-pointer fas fa-bars text-white text-xl" />
                        </div>
                        <div class="hidden md:flex items-center" v-if="!isHiddenMenu">
                            <a href="/account/dashboard" class="text-white mx-2 no-underline font-medium">Home</a>
                            <a data-testid="account-profile" href="/account/profile" class="text-white mx-2 no-underline font-medium">Profile</a>
                            <a href="/account/account" class="text-white mx-2 no-underline font-medium">Account</a>
                            <a v-if="$root.isImpersonating" href="/stop-impersonating" class="text-white mx-2 no-underline font-medium pt-2 pb-2 px-3 border border-gray-50 rounded">Stop</a>
                            <a v-else-if="$root.viewNova" href="/nova" class="text-white mx-2 no-underline font-medium pt-2 pb-2 px-3 border border-gray-50 rounded">Admin</a>
                            <form method="POST" action="/logout">
                                <button type="submit" class="text-white mx-2 no-underline font-medium pt-2 pb-2 px-3 border border-gray-50 rounded">Logout</button>
                            </form>
                        </div>
                    </template>
                </div>
            </slot>

            <div class="mx-auto" :class="`md:${mediumMaxWidthInner}`">

                <slot name="status">
                    <div v-if="showAlert && loan && loan.status === 'past_due' && pastPastDuePayments.length" class="mt-6 text-center bg-danger border-for-danger border-2 rounded-lg py-5 px-14">
                        <p class="font-semibold text-lg text-white">Your loan payment of {{ pastPastDuePayments[0].computed_amount | money(2) }} is currently past due.</p>
                    </div>
                </slot>

                <slot v-if="$slots['floating']" name="floating" />
                <h4 v-else-if="floating" class="mt-8 px-2 md:px-20 font-semibold text-white leading-snug text-center" v-text="floating" />

                <div v-if="hasTop"
                    class="mt-6 shadow-floating bg-white rounded-lg -mb-16 relative z-10">
                    <div v-if="$slots['top-top']" class="w-full app-header-top-top bottom-line" :class="{
                        'px-4 py-3': shortTopHeader,
                        'p-7 md:py-10 md:px-14': ! shortTopHeader
                    }">
                        <slot name="top-top" />
                    </div>

                    <div class="flex flex-col md:flex-row md:items-center">
                        <div class="w-full" :class="{
                            'md:w-1/2': hasBottom
                        }">
                            <div :class="topWrapperClasses">
                                <div class="h-full flex flex-col md:flex-row md:flex-wrap items-center app-header-top"
                                    :class="{
                                        'p-7 pb-2 md:border-r md:border-gray-100': hasBottom,
                                        'p-7 md:py-10 md:px-14': mediumMaxWidthInner === mediumMaxWidth,
                                        'p-7 md:p-8': (mediumMaxWidthInner !== mediumMaxWidth) && ! slimTop,
                                        'p-4 md:p-4': (mediumMaxWidthInner !== mediumMaxWidth) && slimTop,
                                    }">

                                    <div v-if="$slots['top']" class="w-full app-header-top">
                                        <slot name="top" :past-due="loan && loan.status === 'past_due'" />
                                    </div>

                                    <div v-if="$slots['top-left'] || $slots['top-right']" class="w-full">
                                        <div class="flex justify-between mb-4">
                                            <div class="app-header-top-left">
                                                <slot name="top-left" />
                                            </div>
                                            <div class="app-header-top-right font-semibold text-right">
                                                <slot name="top-right" />
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="$slots['top-bottom']" class="w-full app-header-top-bottom">
                                        <slot name="top-bottom" />
                                    </div>

                                </div>
                            </div>
                        </div>
                        <div v-if="hasBottom"
                            class="h-full w-full md:w-1/2 app-header-bottom p-4 md:py-10 md:px-14">

                            <div v-if="$slots['bottom-top']" class="app-header-bottom-top">
                                <slot name="bottom-top" />
                            </div>

                            <div v-if="$slots['bottom-left'] && $slots['bottom-right']" class="flex justify-between">
                                <div class="app-header-bottom-left">
                                    <slot name="bottom-left" />
                                </div>
                                <div class="app-header-bottom-right font-semibold text-right">
                                    <slot name="bottom-right" />
                                </div>
                            </div>

                            <div v-if="$slots['bottom-bottom']" class="app-header-bottom-bottom" :class="{ 'mt-4': $slots['bottom-top'] || ($slots['bottom-left'] && $slots['bottom-right']) }">
                                <slot name="bottom-bottom" />
                            </div>

                        </div>
                    </div>
                </div>

            </div>

        </div>
    </div>
</template>

<script>
    import AppLogo from './../components/AppLogo'; 
    import InteractsWithLoans from '../mixins/InteractsWithLoans';

    export default {
        mixins: [InteractsWithLoans],
        components: {
            AppLogo
        },
        props: {
            isHiddenMenu: {
                type: Boolean,
                default: false
            },
            topWrapperClasses: {
                type: String,
                default: ''
            },
            floating: {},
            loan: {
                type: Object,
                default: () => null
            },
            page: {},
            slimTop: {
                type: Boolean,
                default: false
            },
            showAlert: {
                type: Boolean,
                default: true
            },
            short: {
                type: Boolean,
                default: false
            },
            long: {
                type: Boolean,
                default: false
            },
            shortTopHeader: {
                type: Boolean,
                default: false
            },
            mediumMaxWidth: {
                type: String,
                default: 'max-w-800'
            },
            mediumMaxWidthInner: {
                type: String,
                default: function () {
                    return this.mediumMaxWidth;
                }
            }
        },

        inject: ['openMenu'],

        data() {
            return {
                pastDue: false
            }
        },

        computed: {
            /**
             * @returns {Boolean}
             */
            hasBottom() {
                return !! (this.$slots['bottom-top']
                    || this.$slots['bottom-bottom']
                    || (this.$slots['bottom-left'] && this.$slots['bottom-right']));
            },

            /**
             * @returns {Boolean}
             */
            hasTop() {
                return !! (this.$slots['top-top']
                    || this.$slots['top']
                    || (this.$slots['top-left'] && this.$slots['top-right'])
                    || (this.$slots['slim-top-left'] && this.$slots['slim-top-right']));
            },

            /**
             * @returns {boolean}
             */
            hasBeenApproved() {
                if (!this.loan) return false;
                return [
                    'approved',
                    'agreed',
                    'scheduled',
                ].includes(this.loan.status);
            }
        },
    }
</script>